'use client';

import { c as _c } from "react/compiler-runtime";
import type { PropsWithStyles } from '@morpho-org/ui-v6/types';
import type { SortDirection } from '@tanstack/react-table';
import { cn } from '@morpho-org/ui-v6/utils';
import { Icon, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { Arrow } from '@repo/ui/icons';
import { type JSX, memo, type ReactNode, useMemo } from 'react';
import { Th as CompactTh } from '../CompactTable/Table';
import { Th } from '../LargeTable/Table';
import { useTableVariant } from '../Table/Table';
export interface SortableHeaderCellProps extends PropsWithStyles {
  columnName?: string;
  colSpan: number;
  children: ReactNode;
  isSorted?: false | SortDirection;
  isSortable?: boolean;
  isPinned?: boolean;
  onClick: () => void;
}
export const SortableHeaderCellRoot = t0 => {
  const $ = _c(10);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const tableVariant = useTableVariant();
  if (tableVariant === "compact" || tableVariant === "compact-filter") {
    let t1;
    if ($[3] !== children || $[4] !== props || $[5] !== tableVariant) {
      t1 = <CompactTh tableVariant={tableVariant} {...props}>{children}</CompactTh>;
      $[3] = children;
      $[4] = props;
      $[5] = tableVariant;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  let t1;
  if ($[7] !== children || $[8] !== props) {
    t1 = <Th {...props}>{children}</Th>;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
};
const SortableHeaderCell = t0 => {
  const $ = _c(14);
  const {
    columnName,
    colSpan,
    className,
    children,
    isSorted: t1,
    isSortable: t2,
    onClick
  } = t0;
  const isSorted = t1 === undefined ? false : t1;
  const isSortable = t2 === undefined ? true : t2;
  const tooltipContent = columnName ? `Sort by ${columnName}` : "Sort";
  const t3 = `group/sort inline-flex items-center ${isSortable ? "cursor-pointer" : "cursor-text"}`;
  let t4;
  if ($[0] !== className || $[1] !== isSortable || $[2] !== isSorted || $[3] !== tooltipContent) {
    t4 = isSortable && <Tooltip><TooltipTrigger asChild={true}><div className={cn("flex items-center justify-center", cn("text-secondary-foreground w-5 cursor-pointer", "group-hover/sort:text-foreground group-hover/sort:opacity-100", isSorted ? "opacity-100" : "opacity-0", className))}><Icon icon={<Arrow />} data-is-sorted={isSorted} className="[transform:rotate(90deg)_translateY(-0.5px)] data-[is-sorted=asc]:[transform:rotate(-90deg)]" /></div></TooltipTrigger><TooltipContent>{tooltipContent}</TooltipContent></Tooltip>;
    $[0] = className;
    $[1] = isSortable;
    $[2] = isSorted;
    $[3] = tooltipContent;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== children || $[6] !== onClick || $[7] !== t3 || $[8] !== t4) {
    t5 = <div className={t3} onClick={onClick}>{children}{t4}</div>;
    $[5] = children;
    $[6] = onClick;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== className || $[11] !== colSpan || $[12] !== t5) {
    t6 = <SortableHeaderCellRoot className={className} colSpan={colSpan}>{t5}</SortableHeaderCellRoot>;
    $[10] = className;
    $[11] = colSpan;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
export default memo(SortableHeaderCell);